import { CompactUser } from 'src/context/user';
import { MILESTONE_UPSELL_STORAGE_KEY } from 'src/constants/storageKeys.const';
import { getLocalStorageEntry, setLocalStorageEntry } from 'src/utils/webStorageUtils';

export type MilestoneType = 'streak_7' | 'module_complete';

type MilestoneUpsellStorage = {
  shownByMilestone: Partial<Record<MilestoneType, string>>;
  lastShownAt: string | null;
};

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const emptyStorage = (): MilestoneUpsellStorage => ({
  shownByMilestone: {},
  lastShownAt: null,
});

const readStorage = (userId: number): MilestoneUpsellStorage => {
  const raw = getLocalStorageEntry(`${MILESTONE_UPSELL_STORAGE_KEY}-${userId}`);
  if (!raw) return emptyStorage();

  try {
    const parsed = JSON.parse(raw) as MilestoneUpsellStorage;
    return {
      shownByMilestone: parsed.shownByMilestone ?? {},
      lastShownAt: parsed.lastShownAt ?? null,
    };
  } catch {
    return emptyStorage();
  }
};

const writeStorage = (userId: number, value: MilestoneUpsellStorage) => {
  setLocalStorageEntry(`${MILESTONE_UPSELL_STORAGE_KEY}-${userId}`, JSON.stringify(value));
};

export const canShowMilestoneUpsell = (
  user: CompactUser | null,
  milestone: MilestoneType,
  now = Date.now(),
): boolean => {
  if (!user || user.isPro) return false;

  const storage = readStorage(user.id);
  if (storage.shownByMilestone[milestone]) return false;

  if (storage.lastShownAt) {
    const lastShown = Date.parse(storage.lastShownAt);
    if (!Number.isNaN(lastShown) && now - lastShown < SEVEN_DAYS_MS) return false;
  }

  return true;
};

export const markMilestoneUpsellShown = (
  user: CompactUser | null,
  milestone: MilestoneType,
  now = Date.now(),
) => {
  if (!user) return;

  const storage = readStorage(user.id);
  storage.shownByMilestone[milestone] = new Date(now).toISOString();
  storage.lastShownAt = new Date(now).toISOString();
  writeStorage(user.id, storage);
};

export const getMilestoneSource = (milestone: MilestoneType) =>
  milestone === 'streak_7' ? 'milestone_streak' : 'milestone_module';

/** Read-only accessor for other upsell surfaces that need to cross-check when the
 * milestone upsell last showed (e.g. to avoid showing two upsells on the same day). */
export const getMilestoneUpsellLastShownAt = (userId: number): string | null =>
  readStorage(userId).lastShownAt;
